import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Tooltip, 
  Icon, 
  Popover, 
  Typography, 
  Avatar,
  Chip,
  Stack,
  Divider,
  IconButton,
  TextField,
  Button,
  AvatarGroup
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import { useAppDispatch } from 'src/store';
import { updateConversation } from 'src/features/account/conversations.slice';

interface EmailParticipant {
  Name: string;
  Email: string;
  MailboxHash?: string;
}

interface EmailParticipantsData {
  from: EmailParticipant;
  to: EmailParticipant[];
  cc: EmailParticipant[];
  bcc: EmailParticipant[];
  all: string[];
}

interface ConversationEmailParticipantsProps {
  conversationId: string;
  channelId: string;
  emailParticipants?: EmailParticipantsData;
  conversationMetadata?: Record<string, any>;
  maxAvatars?: number;
}

const ConversationEmailParticipants: React.FC<ConversationEmailParticipantsProps> = ({ 
  conversationId, 
  channelId, 
  emailParticipants,
  conversationMetadata,
  maxAvatars = 3 
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [editedParticipants, setEditedParticipants] = useState<EmailParticipantsData | null>(null);
  const [saving, setSaving] = useState(false);
  
  const isDarkMode = theme.palette.mode === 'dark';
  const open = Boolean(anchorEl);

  // Helpers to normalize varying input shapes (strings or objects, missing fields)
  type ParticipantLike = EmailParticipant | string | null | undefined;
  interface EmailParticipantsInput {
    from?: ParticipantLike;
    to?: ParticipantLike[];
    cc?: ParticipantLike[];
    bcc?: ParticipantLike[];
    all?: string[];
  }

  const toParticipant = (value: ParticipantLike): EmailParticipant => {
    if (!value) return { Name: '', Email: '', MailboxHash: '' };
    if (typeof value === 'string') return { Name: '', Email: value, MailboxHash: '' };
    const obj = value as any;
    return {
      Name: obj.Name || obj.name || '',
      Email: obj.Email || obj.email || obj.address || '',
      MailboxHash: obj.MailboxHash || obj.mailboxHash || undefined,
    };
  };

  const normalizeEmailParticipants = (data?: EmailParticipantsInput | null): EmailParticipantsData | null => {
    if (!data) return null;
    const toArr = Array.isArray(data.to) ? data.to.map(toParticipant).filter(p => !!p.Email) : [];
    const ccArr = Array.isArray(data.cc) ? data.cc.map(toParticipant).filter(p => !!p.Email) : [];
    const bccArr = Array.isArray(data.bcc) ? data.bcc.map(toParticipant).filter(p => !!p.Email) : [];
    const fromObj = data.from ? toParticipant(data.from) : { Name: '', Email: '', MailboxHash: '' };
    const allSet = new Set<string>();
    [fromObj, ...toArr, ...ccArr, ...bccArr].forEach(p => { if (p.Email) allSet.add(p.Email); });
    const all = Array.from(allSet);
    return {
      from: fromObj,
      to: toArr,
      cc: ccArr,
      bcc: bccArr,
      all,
    };
  };

  const normalized = React.useMemo(() => normalizeEmailParticipants(emailParticipants as any), [emailParticipants]);

  // Create display participants for avatars - must be before early return
  const allParticipants = useMemo(() => {
    if (!normalized) return [];
    return [
      ...(normalized.from?.Email ? [{ ...normalized.from, type: 'from' as const }] : []),
      ...(normalized.to || []).map(p => ({ ...p, type: 'to' as const })),
      ...(normalized.cc || []).map(p => ({ ...p, type: 'cc' as const })),
      ...(normalized.bcc || []).map(p => ({ ...p, type: 'bcc' as const })),
    ];
  }, [normalized]);

  // Initialize edited participants when entering edit mode
  React.useEffect(() => {
    if (editing && emailParticipants) {
      const norm = normalizeEmailParticipants(emailParticipants as any);
      if (norm) setEditedParticipants(JSON.parse(JSON.stringify(norm)));
    }
  }, [editing, emailParticipants]);

  if (!normalized) return null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setEditing(false);
    setEditedParticipants(null);
  };

  const getInitials = (participant: EmailParticipant) => {
    if (!participant) return '??';
    if (participant.Name) {
      return participant.Name.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return participant.Email?.slice(0, 2)?.toUpperCase() || '??';
  };

  const getParticipantColor = (type: string) => {
    switch (type) {
      case 'from': return theme.palette.primary.main;
      case 'to': return theme.palette.success.main;
      case 'cc': return theme.palette.info.main;
      case 'bcc': return theme.palette.warning.main;
      default: return theme.palette.grey[500];
    }
  };

  const handleSave = async () => {
    if (!editedParticipants) return;
    
    setSaving(true);
    try {
      // Update the conversation metadata with new email participants
      const updatedMetadata = {
        ...conversationMetadata,
        email_participants: editedParticipants
      };

      await dispatch(updateConversation({
        conversationId,
        channelId,
        conversationData: { metadata: updatedMetadata }
      })).unwrap();

      setEditing(false);
      handleClose();
    } catch (error) {
      console.error('Failed to update conversation email participants:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateParticipantEmail = (
    type: 'to' | 'cc' | 'bcc', 
    index: number, 
    newEmail: string
  ) => {
    if (!editedParticipants) return;
    
    const updated = { ...editedParticipants };
    if (updated[type] && updated[type][index]) {
      updated[type][index] = {
        ...updated[type][index],
        Email: newEmail
      };
      // Update the 'all' array
      const allEmails = new Set<string>();
      if (updated.from?.Email) allEmails.add(updated.from.Email);
      updated.to?.forEach(p => p.Email && allEmails.add(p.Email));
      updated.cc?.forEach(p => p.Email && allEmails.add(p.Email));
      updated.bcc?.forEach(p => p.Email && allEmails.add(p.Email));
      updated.all = Array.from(allEmails);
    }
    setEditedParticipants(updated);
  };

  const removeParticipant = (type: 'to' | 'cc' | 'bcc', index: number) => {
    if (!editedParticipants) return;
    
    const updated = { ...editedParticipants };
    if (updated[type]) {
      updated[type] = updated[type].filter((_, i) => i !== index);
      // Update the 'all' array
      const allEmails = new Set<string>();
      if (updated.from?.Email) allEmails.add(updated.from.Email);
      updated.to?.forEach(p => p.Email && allEmails.add(p.Email));
      updated.cc?.forEach(p => p.Email && allEmails.add(p.Email));
      updated.bcc?.forEach(p => p.Email && allEmails.add(p.Email));
      updated.all = Array.from(allEmails);
    }
    setEditedParticipants(updated);
  };

  const addParticipant = (type: 'to' | 'cc' | 'bcc') => {
    if (!editedParticipants) return;
    
    const updated = { ...editedParticipants };
    if (!updated[type]) {
      updated[type] = [];
    }
    updated[type].push({
      Name: '',
      Email: '',
      MailboxHash: ''
    });
    setEditedParticipants(updated);
  };

  // Compact inline display for conversation header
  const inlineDisplay = (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        py: 0.5,
        px: 1,
        borderRadius: 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        },
      }}
    >
      <Tooltip title="Email participants">
        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Tooltip>
      
      <AvatarGroup
        max={maxAvatars}
        sx={{
          '& .MuiAvatar-root': {
            width: 24,
            height: 24,
            fontSize: '0.7rem',
            border: `2px solid ${isDarkMode ? theme.palette.grey[900] : theme.palette.background.paper}`,
          },
        }}
      >
        {allParticipants.slice(0, maxAvatars).map((participant, idx) => (
          <Tooltip key={idx} title={`${participant.type.toUpperCase()}: ${participant.Email}`}>
            <Avatar sx={{ bgcolor: getParticipantColor(participant.type) }}>
              {getInitials(participant)}
            </Avatar>
          </Tooltip>
        ))}
      </AvatarGroup>

      {allParticipants.length > maxAvatars && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          +{allParticipants.length - maxAvatars}
        </Typography>
      )}
    </Box>
  );

  // Participant row component for popover
  const ParticipantRow = React.memo<{
    label: string;
    participants: EmailParticipant[];
    type: 'to' | 'cc' | 'bcc';
    color: string;
  }>(({ label, participants, type, color }) => {
    const displayParticipants = editing ? editedParticipants?.[type] : participants;
    
    if (!displayParticipants || displayParticipants.length === 0) {
      if (!editing) return null;
    }

    return (
      <Box>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 600,
            display: 'block',
            mb: 0.5
          }}
        >
          {label}
        </Typography>
        <Stack spacing={0.5}>
          {displayParticipants?.map((participant, index) => (
            <Box 
              key={index}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1 
              }}
            >
              {editing ? (
                <>
                  <TextField
                    size="small"
                    value={participant.Email}
                    onChange={(e) => updateParticipantEmail(type, index, e.target.value)}
                    placeholder="email@example.com"
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem',
                        py: 0.5,
                      }
                    }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => removeParticipant(type, index)}
                    sx={{ p: 0.5 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </>
              ) : (
                <Chip
                  size="small"
                  avatar={
                    <Avatar 
                      sx={{ 
                        bgcolor: alpha(color, 0.2),
                        color: color,
                        width: 20,
                        height: 20,
                        fontSize: '0.7rem',
                      }}
                    >
                      {getInitials(participant)}
                    </Avatar>
                  }
                  label={participant?.Name || participant?.Email || 'Unknown'}
                  sx={{
                    height: 24,
                    '& .MuiChip-label': {
                      px: 1,
                      fontSize: '0.75rem',
                    }
                  }}
                />
              )}
            </Box>
          ))}
          {editing && (
            <Button
              size="small"
              startIcon={<Icon sx={{ fontSize: 14 }}>add</Icon>}
              onClick={() => addParticipant(type)}
              sx={{ 
                fontSize: '0.75rem',
                py: 0.25,
                justifyContent: 'flex-start',
                color: 'text.secondary',
              }}
            >
              Add {label.toLowerCase()}
            </Button>
          )}
        </Stack>
      </Box>
    );
  });
  
  ParticipantRow.displayName = 'ParticipantRow';

  return (
    <>
      {inlineDisplay}
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 320,
            maxWidth: 400,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <Box>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2 
          }}>
            <Typography variant="subtitle2" fontWeight={600}>Conversation Email Participants</Typography>
            {!editing ? (
              <IconButton 
                size="small" 
                onClick={() => setEditing(true)}
                sx={{ ml: 1 }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  <CheckIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton 
                  size="small"
                  onClick={() => {
                    setEditing(false);
                    setEditedParticipants(null);
                  }}
                  disabled={saving}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* From */}
          {normalized.from?.Email && (
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontWeight: 600,
                  display: 'block',
                  mb: 0.5
                }}
              >
                FROM
              </Typography>
              <Chip
                size="small"
                avatar={
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      color: theme.palette.primary.main,
                      width: 20,
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  >
                    {getInitials(normalized.from)}
                  </Avatar>
                }
                label={normalized.from?.Name || normalized.from?.Email || 'Unknown'}
                sx={{
                  height: 24,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.75rem',
                  }
                }}
              />
            </Box>
          )}

          {/* Recipients */}
          <Stack spacing={2} divider={<Divider />}>
            <ParticipantRow
              label="TO"
              participants={normalized.to || []}
              type="to"
              color={theme.palette.success.main}
            />
            <ParticipantRow
              label="CC"
              participants={normalized.cc || []}
              type="cc"
              color={theme.palette.info.main}
            />
            {(normalized.bcc?.length > 0 || editing) && (
              <ParticipantRow
                label="BCC"
                participants={normalized.bcc || []}
                type="bcc"
                color={theme.palette.warning.main}
              />
            )}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default ConversationEmailParticipants;
